import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Dropdown} from '@/components/atoms'
import ProductSelectionModal from './ProductSelectionModal'
import {BONUS_PRODUCTS_DROPDOWN_OPTIONS, BONUS_PRODUCTS_DROPDOWN_OPTIONS_WITH_GTIN} from './config'
import styles from '../RewardSetup.module.scss'
import {showGtinFeature} from '@/utils/commons'
import {useAppSelector} from '@/store'
import PointsCondition from './PointsCondition'
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants'

/**
 * ProductOrPepVoucherCondition component displays the product or PEP voucher reward condition UI
 * Handles product selection via modal triggered by dropdown option selection
 * For PRODUCT type: uses handleProductsChange and selectedProducts field
 * For PEP_VOUCHER_PRODUCT type: uses handlePepVouchersChange and selectedPepVouchersProducts field
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing selectedProducts or selectedPepVouchersProducts
 * @param {Object} props.validation - Validation state object
 * @param {boolean} props.pepVouchersOrProductsLoading - Loading state for products or PEP vouchers
 * @param {Function} props.handleProductsChange - Handler for products selection changes (PRODUCT type)
 * @param {Function} props.handlePepVouchersChange - Handler for PEP vouchers selection changes (PEP_VOUCHER_PRODUCT type)
 * @returns {JSX.Element} The rendered ProductOrPepVoucherCondition component
 */
const ProductOrPepVoucherCondition = ({
  formData,
  validation,
  pepVouchersOrProductsLoading,
  handleProductsChange,
  handlePepVouchersChange,
  handlePointsChange,
  pointsError,
}) => {
  const [showProductModal, setShowProductModal] = useState(false)
  
  // Determine which handler and field to use based on reward type
  const isProductType = formData.rewardType === REWARD_TYPES.PRODUCT
  const handleChange = isProductType ? handleProductsChange : handlePepVouchersChange
  const selectedProducts = isProductType 
    ? (formData.selectedProducts || [])
    : (formData.selectedPepVouchersProducts || [])
  const validationKey = isProductType ? 'selectedProductsValid' : 'selectedPepVouchersProductsValid'
  
  // Handle dropdown option selection
  const handleDropdownClick = selectedValue => {
    // Filter out Product ID and GTIN options from the selected value array
    // These are action options, not actual product selections
    const filteredSelectedValue = selectedValue
      ? selectedValue.filter(item => item.id !== 'productId' && item.id !== 'productId/gtin')
      : []
      
    // Pass the filtered array to the appropriate handler
    if (handleChange) {
      handleChange(filteredSelectedValue)
    }

    // Check if Product ID or GTIN options were selected for modal/action handling
    if (selectedValue && selectedValue.length > 0) {
      setShowProductModal(true)
    }
  }

  // Handle closing the product selection modal
  const handleCloseProductModal = () => {
    setShowProductModal(false)
  }

  // Handle product selection from modal
  const handleProductSelection = selectedProductsData => {
    // Convert selected products to the format expected by the parent component
    // The parent expects an array of objects with value and label properties
    const selectedProductValues = selectedProductsData.map(product => ({
      value: product.productId,
      label: product.productName,
    }))

    if (handleChange) {
      handleChange(selectedProductValues)
    }
  }
  const countryCode = useAppSelector(state => state.countryCode).countryCode

  // Determine if GTIN feature should be shown based on country code
  const showGtin = showGtinFeature(countryCode)

  // Choose dropdown options based on GTIN feature flag
  const bonusProductDropdownOptions = showGtin
    ? BONUS_PRODUCTS_DROPDOWN_OPTIONS_WITH_GTIN
    : BONUS_PRODUCTS_DROPDOWN_OPTIONS

  // Create display options for dropdown based on selected products
  // Uses selectedProducts for PRODUCT type, selectedPepVouchersProducts for PEP_VOUCHER_PRODUCT type
  const selectedProductOptions = selectedProducts
  // Create dynamic placeholder based on selected products
  const getPlaceholder = () => {
    if (pepVouchersOrProductsLoading) return 'Loading...'
    
    if (selectedProductOptions.length === 0) return 'Select Products'
    
    if (selectedProductOptions.length === 1) {
      return selectedProductOptions[0].label
    }
  }

  return (
    <div className="flex flex-wrap gap-[13px]">
      <div className={`${styles.rewardSetup__productsDropdownWrapper} text-left`}>
        <Dropdown
          className="text-sm placeholder:text-sm"
          value={selectedProductOptions} // Show selected products
          isError={validation[validationKey]}
          errorMessage={validation[validationKey] ? 'At least one product must be selected' : ''}
          isDark={true}
          childList={bonusProductDropdownOptions}
          placeholder={getPlaceholder()}
          isJsonWrap={false}
          selection="multiple"
          hideMenuListOnSelection={true}
          isClearable={false}
          updateValue={handleDropdownClick}
          style={{width:'260px'}}
        />
      </div>

      <PointsCondition formData={formData} handlePointsChange={handlePointsChange} pointsError={pointsError} />
      {/* Product Selection Modal */}
      <ProductSelectionModal
        showModal={showProductModal}
        onCloseModal={handleCloseProductModal}
        onSelectProducts={handleProductSelection}
        selectedProducts={
          selectedProducts?.map(p => ({
            productId: p.value,
            productName: p.displayText || p.label,
          })) || []
        }
        rewardType={formData.rewardType}
      />
    </div>
  )
}

ProductOrPepVoucherCondition.propTypes = {
  formData: PropTypes.shape({
    rewardType: PropTypes.string,
    selectedProducts: PropTypes.array,
    selectedPepVouchersProducts: PropTypes.array,
    points: PropTypes.string,
  }).isRequired,
  validation: PropTypes.shape({
    selectedProductsValid: PropTypes.bool,
    selectedPepVouchersProductsValid: PropTypes.bool,
  }).isRequired,
  pepVouchersOrProductsLoading: PropTypes.bool.isRequired,
  handleProductsChange: PropTypes.func,
  handlePepVouchersChange: PropTypes.func,
  handlePointsChange: PropTypes.func,
  pointsError: PropTypes.string,
}

export default ProductOrPepVoucherCondition
